import { UserRequest, UserResponse, UserStatus } from "@app/types/user.types";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env/environment";
import { Observable } from "rxjs";
import { Utils } from "@shared/utils/utils";
import {
  ApiResponse,
  ApiResponsePageable,
  DEFAULT_SEARCH_TAKE,
  DeleteApiResponse,
  PageControl,
} from "@app/types/api.types";

@Injectable({
  providedIn: "root",
})
export class UserService {
  constructor(private readonly _http: HttpClient) {}

  public getUsers(
    pageControl: PageControl,
    filters?: any,
  ): Observable<ApiResponsePageable<UserResponse>> {
    const paginate = Utils.mountPageControl(pageControl, DEFAULT_SEARCH_TAKE);
    const filterParams = Utils.mountPageControl(filters);

    return this._http.get<ApiResponsePageable<UserResponse>>(
      `${environment.apiUrl}/user/search?${paginate}${filterParams}`,
    );
  }

  public getUser(): Observable<ApiResponse<UserResponse>> {
    return this._http.get<ApiResponse<UserResponse>>(
      `${environment.apiUrl}/user/me`,
    );
  }

  public userById(id: string): Observable<ApiResponse<UserResponse>> {
    return this._http.get<ApiResponse<UserResponse>>(
      `${environment.apiUrl}/user/${id}`,
    );
  }

  public postUser(
    token: string,
    user: UserRequest | FormData,
  ): Observable<ApiResponse<UserResponse>> {
    return this._http.post<ApiResponse<UserResponse>>(
      `${environment.apiUrl}/auth/user/${token}`,
      user,
    );
  }

  public patchUser(
    id: string,
    user: FormData,
  ): Observable<ApiResponse<UserResponse>> {
    return this._http.post<ApiResponse<UserResponse>>(
      `${environment.apiUrl}/user/${id}?_method=PATCH`,
      user,
    );
  }

  public updateStatus(
    id: string,
    newStatus: string | UserStatus,
  ): Observable<ApiResponse<UserResponse>> {
    return this._http.patch<ApiResponse<UserResponse>>(
      `${environment.apiUrl}/user/status/${id}`,
      { status: newStatus },
    );
  }

  public deleteUser(id: string): Observable<DeleteApiResponse> {
    return this._http.delete<DeleteApiResponse>(
      `${environment.apiUrl}/user/${id}`,
    );
  }

  public inviteUser(invite: {
    email?: string;
    phone?: number;
  }): Observable<ApiResponse<string>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/user/invite`,
      invite,
    );
  }

  public generateRegisterLink(): Observable<ApiResponse<string>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/user/link`,
      {},
    );
  }

  public recoverPassword(email: string): Observable<ApiResponse<string>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/auth/user/recover-password`,
      { email: email },
    );
  }

  public resetPassword(
    token: string,
    password: string,
  ): Observable<ApiResponse<string>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/auth/user/reset-password/${token}`,
      { password, password_confirmation: password },
    );
  }

  public validateCode(code: number): Observable<ApiResponse<string>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/auth/user/check-token`,
      { code },
    );
  }

  public validateToken(token: string): Observable<ApiResponse<any>> {
    return this._http.post<ApiResponse<string>>(
      `${environment.apiUrl}/auth/user/check-token`,
      { token: token },
    );
  }

  public deleteUserFiles(files: string[]): Observable<DeleteApiResponse> {
    return this._http.post<DeleteApiResponse>(
      `${environment.apiUrl}/user/delete-files`,
      { user_files: files },
    );
  }
}
